var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskMessage, ByteStreamWriter, MulticastDelegate, MulticastDelegate1, TYPE_HASH_BYTES_PER_ELEMENT, ServerBufferMessageType, AskMessageErrorCode } from "flavr-common";
import { ServerTimeMessage } from "@messages/session/SessionMessages";
import { busService } from "@modules/bus/BusService";
import { soundService } from "@modules/audio/SoundService";
import { createKeepAliveBuffer } from "./ServerBufferMessagesUtils";
import { MessageService } from "../../../ui/services/message.service";
const KEEP_ALIVE_INTERVAL = 10000;
const RECONNECT_INTERVAL = 5000;
const NORMAL_CLOSURE_CODE = 1000;
// handle a Web socket connection to communicate with the session server
class ServerConnection {
    constructor() {
        this.connection = busService.connection("ServerConnection");
        this.messageDelegate = new MulticastDelegate1();
        this.openDelegate = new MulticastDelegate();
        this.closeDelegate = new MulticastDelegate1();
        this.buffer = [];
        this.pendingMessages = [];
        this.pendingBusMessages = [];
        this.pendingAskMessages = [];
        this.waitForServerInitData = true;
        this.isReady = false;
        this.pendingNotReadyMessages = [];
        this.reconnectEnabled = true;
        this.avgServerTimeOffset = 0;
        this.serverTimeRequests = 0;
        this.timeOffsets = [];
        this.isConnectionToServerLost = false;
        this.onWaitForReadyMessage = (message) => {
            this.pendingNotReadyMessages.push(message);
        };
        this.onMessage = (message) => {
            if (typeof message.data === "string") {
                const msg = JSON.parse(message.data);
                this.messageDelegate.invoke(msg);
            }
            else if (message.data instanceof ArrayBuffer) {
                this.messageDelegate.invoke({
                    channel: "session:arrayBuffer",
                    payload: message.data
                });
            }
        };
        this.socket = this.connect();
        this.updateTimeOffset();
        this.messageDelegate.add((msg) => {
            if (msg.channel === "connected") {
                for (const msg of this.buffer) {
                    this.send(msg);
                }
                this.buffer = [];
            }
        });
    }
    connect() {
        const scheme = location.protocol === "http:" ? "ws" : "wss";
        this.socket = new WebSocket(`${scheme}://${location.host}/server`);
        this.socket.binaryType = "arraybuffer";
        this.socket.onopen = () => {
            this.openDelegate.invoke();
            if (this.isConnectionToServerLost) {
                this.isConnectionToServerLost = false;
                soundService.playServerConnectionStateChangedSound(false);
            }
            this.startKeepAlive();
        };
        if (this.isReady) {
            this.socket.onmessage = this.onMessage;
        }
        else {
            this.socket.onmessage = this.onWaitForReadyMessage;
        }
        this.socket.onclose = (event) => {
            // this.isReady = false;
            this.closeDelegate.invoke(event.code !== undefined ? event.code : -1);
            this.cancelKeepAlive();
            if (event.reason) {
                MessageService.showMessage("error", `Websocket closed for reason: ${event.reason}`);
            }
            // Normal Closure
            if (event.code === NORMAL_CLOSURE_CODE) {
                console.warn(`[multiplayer] Websocket closed for reason: ${event.reason}`);
                return;
            }
            else if (event.code === 4001) {
                console.warn(`[multiplayer] Websocket closed for reason: ${event.reason}`);
                return;
            }
            console.warn("[multiplayer]", event);
            if (!this.isConnectionToServerLost) {
                soundService.playServerConnectionStateChangedSound(true);
                this.isConnectionToServerLost = true;
            }
            if (this.reconnectEnabled) {
                this.reconnect();
            }
        };
        return this.socket;
    }
    reconnect() {
        setTimeout(() => {
            console.log("[multiplayer] WebSocket: reconnecting...");
            this.connect();
        }, RECONNECT_INTERVAL);
    }
    readyToReceiveMessages() {
        if (this.isReady)
            return;
        this.isReady = true;
        for (const msg of this.pendingNotReadyMessages) {
            this.onMessage(msg);
        }
        this.pendingNotReadyMessages = [];
        this.socket.onmessage = this.onMessage;
        if (this.serverTimeRequests > 10) {
            this.connection.send(new ServerTimeMessage(this.avgServerTimeOffset), undefined, Date.now());
        }
    }
    close() {
        this.cancelKeepAlive();
        if (this.socket != undefined) {
            this.socket.close();
        }
    }
    isServerOnline() {
        return this.socket.readyState === WebSocket.OPEN;
    }
    isServerOffline() {
        return this.socket.readyState === WebSocket.CLOSED || this.socket.readyState === WebSocket.CLOSING;
    }
    addOnlineStateChangedListener(onlineStateChangedCallback) {
        this.socket.addEventListener("open", () => {
            onlineStateChangedCallback(true);
        });
        this.socket.addEventListener("close", () => {
            onlineStateChangedCallback(false);
        });
    }
    send(msg) {
        if (this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(msg));
        }
        else {
            this.buffer.push(msg);
        }
    }
    queueMessage(msg) {
        this.pendingMessages.push(msg);
    }
    queueBusMessage(msg, timestamp) {
        if (AskMessage.is(msg)) {
            return;
        }
        this.pendingBusMessages.push({
            msg,
            timestamp: timestamp || Date.now()
        });
    }
    queueAskMessage(msg, askId, timestamp) {
        this.pendingAskMessages.push({
            msg,
            askId,
            timestamp: timestamp || Date.now()
        });
    }
    flushPendingMessages() {
        if (this.socket.readyState !== WebSocket.OPEN)
            return;
        if (this.pendingAskMessages.length) {
            const buffer = this.packAskMsgInArrayBuffer(this.pendingAskMessages);
            if (buffer) {
                this.sendArrayBuffer(buffer);
            }
            this.pendingAskMessages = [];
        }
        if (this.pendingBusMessages.length) {
            const buffer = this.packMsgInArrayBuffer(this.pendingBusMessages, this.pendingMessages);
            if (buffer) {
                this.sendArrayBuffer(buffer);
            }
            this.pendingBusMessages = [];
        }
        if (this.pendingMessages.length) {
            serverConnection.send({
                channel: "bulk",
                payload: this.pendingMessages
            });
            this.pendingMessages = [];
        }
    }
    sendArrayBuffer(buffer) {
        this.socket.send(buffer);
    }
    updateTimeOffset() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const clientSentTime = Date.now() + this.avgServerTimeOffset;
                const res = yield fetch(document.location.href, {
                    method: "HEAD",
                    cache: "no-cache"
                });
                const clientReceivedTime = Date.now();
                const resTime = res.headers.get("Date");
                if (resTime == null)
                    return;
                const precision = 1000;
                const serverReceivedTime = new Date(resTime).getTime() + precision / 2;
                const serverTime = serverReceivedTime + (clientReceivedTime - clientSentTime) / 2;
                const timeOffset = serverTime - clientReceivedTime;
                this.serverTimeRequests++;
                if (this.serverTimeRequests <= 10) {
                    this.timeOffsets.push(timeOffset);
                }
                else {
                    this.timeOffsets[this.serverTimeRequests % 10] = timeOffset;
                }
                this.avgServerTimeOffset = this.timeOffsets.reduce((acc, offset) => acc + offset, 0) / this.timeOffsets.length;
                if (this.isReady && (this.serverTimeRequests >= 10 || this.serverTimeRequests === 1)) {
                    this.connection.send(new ServerTimeMessage(this.avgServerTimeOffset), undefined, clientReceivedTime);
                }
                if (this.serverTimeRequests > 10) {
                    setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                        yield this.updateTimeOffset();
                    }), 5 * 60 * 1000); // sync every 5 minutes
                }
                else {
                    this.updateTimeOffset();
                }
            }
            catch (error) {
                console.error("[multiplayer]", error);
            }
        });
    }
    startKeepAlive() {
        this.cancelKeepAlive();
        this.keepAliveIntervalId = window.setInterval(() => {
            if (this.socket.readyState !== WebSocket.OPEN)
                return;
            this.sendArrayBuffer(createKeepAliveBuffer());
        }, KEEP_ALIVE_INTERVAL);
    }
    cancelKeepAlive() {
        if (this.keepAliveIntervalId === undefined)
            return;
        window.clearInterval(this.keepAliveIntervalId);
        this.keepAliveIntervalId = undefined;
    }
    packAskMsgInArrayBuffer(messages) {
        let byteLength = Uint8Array.BYTES_PER_ELEMENT + Uint32Array.BYTES_PER_ELEMENT; // msgType // msgCount
        let msgCount = 0;
        for (const data of messages) {
            const msg = data.msg.message;
            const bL = msg.byteLength;
            if (bL === undefined) {
                throw new Error(`Ask message "${msg.type}" to server has no defined byteLength`);
            }
            byteLength +=
                Float64Array.BYTES_PER_ELEMENT + // timestamp
                    Uint32Array.BYTES_PER_ELEMENT + // askId
                    TYPE_HASH_BYTES_PER_ELEMENT + // typeHash
                    bL;
            ++msgCount;
        }
        if (msgCount === 0)
            return;
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(ServerBufferMessageType.AskMsg);
        stream.uint32(msgCount);
        for (const data of messages) {
            stream.float64(data.timestamp);
            stream.uint32(data.askId);
            const msg = data.msg.message;
            if (!msg.serialization) {
                throw new Error(`Ask message "${msg.type}" to server is not serializable`);
            }
            if (!msg.typeHash) {
                throw `Ask message "${msg.type}" to server has no defined typeHash`;
            }
            stream.typeHash(msg.typeHash);
            msg.serialization(stream);
        }
        return buffer;
    }
    createAnswerErrorBuffer(askId, errorCode) {
        const byteLength = Uint8Array.BYTES_PER_ELEMENT + // msgType
            Uint32Array.BYTES_PER_ELEMENT + // askId
            TYPE_HASH_BYTES_PER_ELEMENT + // typeHash
            Uint8Array.BYTES_PER_ELEMENT; // errorCode
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(ServerBufferMessageType.AnswerMsg);
        stream.uint32(askId);
        stream.uint8(errorCode || AskMessageErrorCode.Unknown);
        return buffer;
    }
    packAnswerInArrayBuffer(msg, askId) {
        const msgLength = msg.byteLength;
        if (!msg.serialization || !msg.typeHash || msgLength === undefined) {
            return undefined;
        }
        const byteLength = Uint8Array.BYTES_PER_ELEMENT + // msgType
            Uint32Array.BYTES_PER_ELEMENT + // askId
            TYPE_HASH_BYTES_PER_ELEMENT + // typeHash
            Uint8Array.BYTES_PER_ELEMENT + // errorCode
            msgLength;
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(ServerBufferMessageType.AnswerMsg);
        stream.uint32(askId);
        stream.uint8(0);
        stream.typeHash(msg.typeHash);
        msg.serialization(stream);
        return buffer;
    }
    packMsgInArrayBuffer(messages, fallbackMessages) {
        let byteLength = Uint8Array.BYTES_PER_ELEMENT + Uint32Array.BYTES_PER_ELEMENT; // msgType // msgLength
        let msgLength = 0;
        for (const data of messages) {
            if (data.msg.typeHash) {
                const bL = data.msg.byteLength;
                if (bL === undefined)
                    throw `byteLength is not defined in message ${data.msg.type}`;
                byteLength +=
                    TYPE_HASH_BYTES_PER_ELEMENT + // typeHash
                        Float64Array.BYTES_PER_ELEMENT + // timestamp
                        bL;
                ++msgLength;
            }
        }
        if (msgLength === 0) {
            const newArray = messages.map((data) => ({
                channel: "bus",
                payload: {
                    msg: data.msg,
                    ts: data.timestamp
                }
            }));
            fallbackMessages.push(...newArray);
            return;
        }
        const buffer = new ArrayBuffer(byteLength);
        const stream = new ByteStreamWriter(buffer);
        stream.uint8(ServerBufferMessageType.BusMsg);
        stream.uint32(msgLength);
        for (const data of messages) {
            if (data.msg.serialization && data.msg.typeHash) {
                stream.typeHash(data.msg.typeHash);
                stream.float64(data.timestamp);
                data.msg.serialization(stream);
            }
            else {
                if (!fallbackMessages) {
                    console.warn("[multiplayer] packMsgInArrayBuffer: Message doesn't send", data.msg.type);
                    continue;
                }
                fallbackMessages.push({
                    channel: "bus",
                    payload: {
                        msg: data.msg,
                        ts: data.timestamp
                    }
                });
            }
        }
        return buffer;
    }
}
export const serverConnection = new ServerConnection();
